import React, { useState, useEffect, useMemo } from 'react';
import { Formik } from 'formik';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import ColorPicker from '@salesforce/design-system-react/components/color-picker';
import Textarea from '@salesforce/design-system-react/components/textarea';
import Input from '@salesforce/design-system-react/components/input';
import { Combobox, Button } from '@salesforce/design-system-react';
import { accountDetailsValidation } from './accountDetailsValidation';
import { useDispatch, useSelector } from 'react-redux';
import {
  findAccountById,
  accountsSelector,
  updateAccount,
  deleteAccount,
} from '../../../store/accounts/slice';
import { defiAccountCategories, defiAccountTypes } from '../../../constants/defiAccount';
import { getDateTimeFromTimestamp } from '../../../utilities/datetime';
import { getNumberWithCommas } from '../../../utilities/money';
import toast, { Toaster } from 'react-hot-toast';
import Spinner from '../../../components/Spinner';
import ActionRemove from '../../../components/Modal/actionRemove';
import { ACTION_TYPES } from '../../../constants/types';
import { getSyncTime } from '../../../services/snapshot';
import { useNavigate } from 'react-router-dom';
import comboboxFilterAndLimit from '@salesforce/design-system-react/components/combobox/filter';

const defaultTagOptions = [
  {
    id: 'trading',
    label: 'Trading',
  },
  {
    id: 'skating',
    label: 'Skating',
  },
  {
    id: 'tomb',
    label: 'Tomb',
  },
];

const DetailsTab = ({ account }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { singleAccount, loading, updateSuccess, message } = useSelector(accountsSelector);
  const [formData, setFormData] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [actionRemoveModal, setActionRemoveModal] = useState({
    open: false,
    type: '',
  });
  const [loadingRemove, setLoadingRemove] = useState(false);
  const [tagInputText, setTagInputText] = useState('');
  const [tagSelection, setTagSelection] = useState([]);
  const isSnapshot = useMemo(() => !!getSyncTime(), []);

  useEffect(() => {
    dispatch(findAccountById(account));
  }, [account, dispatch]);

  useEffect(() => {
    if (updateSuccess && showToast) {
      console.log('setting toast');
      toast.success(message, {
        id: 'details-toast',
      });
      setShowToast(false);
    }
  }, [updateSuccess, showToast, message]);

  useEffect(() => {
    setFormData(singleAccount);
    if (singleAccount.tags) {
      setTagSelection(
        singleAccount.tags?.split(',').map((item) => ({ id: item?.toLowerCase(), label: item }))
      );
    }
  }, [singleAccount]);

  function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
  }

  const handleRemove = () => {
    // Remove operation
    setActionRemoveModal({
      open: true,
      name: formData.name,
      id: formData._id,
      type: ACTION_TYPES.REMOVE,
      title: 'wallet',
    });
  };

  const removeAccount = (id) => {
    // dispatch remove account API
    if (id) {
      setLoadingRemove(true);
      const accountDetails = {
        id: id,
      };
      dispatch(deleteAccount(accountDetails)).then((res) => {
        if (res?.payload?.status === 200) {
          // setShowToast(true);
          setActionRemoveModal({
            open: false,
            type: '',
          });
          setLoadingRemove(false);
          navigate('/defi/accounts');
        }
      });
    }
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        accountName: formData.name || '',
        accountAddress: formData.wallet_address || '',
        dateAdded: getDateTimeFromTimestamp(formData.createdAt) || '--',
        balanceOnDateAdded: formData.balance_on_date_added
          ? `$${getNumberWithCommas(formData.balance_on_date_added)}`
          : '--',
        category: formData.category ? [{ label: formData.category }] : '',
        type: formData.type ? [{ label: formData.type }] : '',
        description: formData.description || '',
        tags: formData.tags || '',
        color: formData.account_color || '',
      }}
      validationSchema={accountDetailsValidation}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        setShowToast(true);
        setSubmitting(true);
        const data = {
          category: values?.category[0]?.label || '',
          type: values?.type[0]?.label || '',
          description: values?.description || '',
          tags: tagSelection?.map((item) => item.label).join(','),
          account_color: values?.color || '',
        };

        /**
         * check if key is empty, and remove empty key.
         */
        Object.keys(data).forEach((key) => {
          if (!formData) {
            if (!data[key]) delete data[key];
          }
        });

        const accountDetails = {
          id: formData._id,
          data: data,
        };

        setSubmitting(false);
        dispatch(updateAccount(accountDetails));
      }}
    >
      {(props) => {
        const {
          values,
          touched,
          errors,
          isSubmitting,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
        } = props;
        return (
          <>
            <form
              onSubmit={handleSubmit}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleSubmit();
                }
              }}
              className="home_card"
            >
              <Toaster position="top-right" reverseOrder={false} />
              <div className="" style={{ minHeight: 'calc(100vh - 340px)', marginTop: '10px' }}>
                <div className="add-form">
                  {loading && !loadingRemove ? (
                    <Spinner />
                  ) : (
                    <section className="slds-p-around_large">
                      <div className="two-grid-layout slds-grid_pull-padded slds-grid_vertical-align-center">
                        {/* <div className="slds-col_padded slds-m-bottom_large">
                      <p>Account</p>
                      <p style={{ color: 'blue', fontWeight: '700' }}>Trading Wallet</p>
                    </div> */}
                        <div className="slds-col_padded slds-m-bottom_large">
                          <Input
                            id="accountName"
                            name="accountName"
                            label="Account Name"
                            placeholder="Enter Account Name"
                            readOnly
                            value={values.accountName || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={classNames(
                              'bold-input',
                              errors.accountName && touched.accountName && 'slds-has-error'
                            )}
                            styleInput={{ paddingLeft: '0.75rem' }}
                          />
                          {errors.accountName && touched.accountName && (
                            <div className="slds-form-element__help">{errors.accountName}</div>
                          )}
                        </div>

                        <div className="slds-col_padded slds-m-bottom_large">
                          <Input
                            id="accountAddress"
                            name="accountAddress"
                            label="Account Address"
                            placeholder="Enter Account Address"
                            readOnly
                            value={values.accountAddress || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={classNames(
                              'bold-input',
                              errors.accountAddress && touched.accountAddress && 'slds-has-error'
                            )}
                            styleInput={{ paddingLeft: '0.75rem' }}
                          />
                          {/* {errors.accountAddress && touched.accountAddress && (
                        <div className="slds-form-element__help">{errors.accountAddress}</div>
                      )} */}
                        </div>

                        {/* <div className="slds-col_padded slds-m-bottom_large">
                      <p>Account</p>
                      <p style={{ color: 'blue', fontWeight: '700' }}>Trading Wallet</p>
                    </div> */}
                      </div>
                      {/* <div style={{ display: 'flex' }}>
                    <div style={{ width: '50%' }}>
                      <p>Date Added</p>
                      <p style={{ fontWeight: '700' }}>10/16/2020 23:43:23</p>
                    </div>
                    <div className="slds-col_padded slds-m-bottom_large">
                      <p>Account Balance</p>
                      <p style={{ fontWeight: '700' }}>$456,356.89</p>
                    </div>
                  </div> */}

                      <div className="two-grid-layout slds-grid_pull-padded slds-grid_vertical-align-center">
                        <div className="slds-col_padded slds-m-bottom_large">
                          <Input
                            id="dateAdded"
                            name="dateAdded"
                            label="Date Added"
                            readOnly
                            value={values.dateAdded || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={classNames(
                              'bold-input bold color-dark',
                              errors.dateAdded && touched.dateAdded && 'slds-has-error'
                            )}
                            styleInput={{ paddingLeft: '0.75rem' }}
                          />
                          {errors.dateAdded && touched.dateAdded && (
                            <div className="slds-form-element__help">{errors.dateAdded}</div>
                          )}
                        </div>

                        <div className="slds-col_padded slds-m-bottom_large">
                          <Input
                            id="balanceOnDateAdded"
                            name="balanceOnDateAdded"
                            label="Account Balance on Date Added"
                            readOnly
                            value={values.balanceOnDateAdded || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={classNames(
                              'bold-input color-dark',
                              errors.balanceOnDateAdded &&
                                touched.balanceOnDateAdded &&
                                'slds-has-error'
                            )}
                            styleInput={{ paddingLeft: '0.75rem' }}
                          />
                          {/* {errors.balanceOnDateAdded && touched.balanceOnDateAdded && (
                        <div className="slds-form-element__help">{errors.balanceOnDateAdded}</div>
                      )} */}
                        </div>
                      </div>

                      <div className="two-grid-layout slds-grid_pull-padded slds-grid_vertical-align-center">
                        <div className="slds-col_padded slds-m-bottom_large">
                          <IconSettings iconPath="/assets/icons/utility/down_60.png">
                            <Combobox
                              id="category"
                              name="category"
                              events={{
                                onSelect: (event, data) => {
                                  setFieldValue('category', data.selection);
                                },
                              }}
                              labels={{
                                label: 'Category',
                                placeholder: 'Select Category',
                              }}
                              options={defiAccountCategories}
                              selection={values.category}
                              variant="readonly"
                            />
                          </IconSettings>
                        </div>
                        <div className="slds-col_padded slds-m-bottom_large">
                          <Combobox
                            id="type"
                            name="type"
                            events={{
                              onSelect: (event, data) => {
                                setFieldValue('type', data.selection);
                              },
                            }}
                            labels={{
                              label: 'Type',
                              placeholder: 'Select Type',
                            }}
                            options={defiAccountTypes}
                            selection={values.type}
                            variant="readonly"
                          />
                        </div>
                      </div>

                      <div className="two-grid-layout slds-grid_pull-padded slds-grid_vertical-align-center">
                        <div className="slds-col_padded" style={{ alignSelf: 'normal' }}>
                          <div className="slds-form-element">
                            <div className="slds-form-element__control">
                              <Textarea
                                id="description"
                                name="description"
                                label="Description"
                                placeholder="Enter description"
                                value={values.description || ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="slds-col_padded">
                          <div className=" slds-m-bottom_large">
                            <IconSettings iconPath="/assets/icons">
                              <Combobox
                                id="tags"
                                name="tags"
                                optionsAddItem={
                                  tagInputText.length > 0
                                    ? [
                                        {
                                          id: 'add-new-tag',
                                          label: (inputValue) => [
                                            inputValue && inputValue.length > 0 ? (
                                              <span style={{ marginLeft: '-30px' }}>
                                                Create "{inputValue}"
                                              </span>
                                            ) : (
                                              ''
                                            ),
                                          ],
                                        },
                                      ]
                                    : []
                                }
                                events={{
                                  onChange: (event, data) => {
                                    setTagInputText(data.value);
                                  },
                                  onSelect: (event, data) => {
                                    const { selection } = data;
                                    if (selection && selection.length > 0) {
                                      if (
                                        selection[selection.length - 1].type &&
                                        selection[selection.length - 1].type === 'footer'
                                      ) {
                                        if (tagInputText.length > 0) {
                                          setTagSelection(
                                            tagSelection.concat({ label: tagInputText })
                                          );
                                          setTagInputText('');
                                        }
                                      } else {
                                        setTagSelection(selection);
                                      }
                                    } else {
                                      setTagSelection(selection.pop());
                                    }
                                  },
                                  onSubmit: (event, data) => {
                                    if (!data.value.trim()) return;
                                    setTagSelection(tagSelection.concat({ label: data.value }));
                                    setTagInputText('');
                                  },
                                  onRequestRemoveSelectedOption: (event, data) => {
                                    setTagSelection(data.selection);
                                  },
                                }}
                                labels={{
                                  label: 'Tags',
                                  placeholder: 'Enter tags',
                                }}
                                options={comboboxFilterAndLimit({
                                  inputValue: tagInputText,
                                  limit: 5,
                                  options: defaultTagOptions,
                                  selection: tagSelection,
                                })}
                                value={tagInputText}
                                selection={tagSelection}
                                multiple
                              />
                            </IconSettings>
                            <div className="slds-m-top_medium">
                              <IconSettings iconPath="/assets/icons">
                                <ColorPicker
                                  labels={{ label: 'Color' }}
                                  id="color"
                                  name="color"
                                  className={'slds-form-element__label'}
                                  menuPosition="overflowBoundaryElement"
                                  value={values.color || ''}
                                  events={{
                                    onChange: (event, { color }) => {
                                      setFieldValue('color', color);
                                      setFormData({
                                        ...formData,
                                        color,
                                      });
                                    },
                                  }}
                                />
                              </IconSettings>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <Button
                          label={loading ? <span>Loading</span> : <span>Update</span>}
                          disabled={isSubmitting || isSnapshot}
                          type="submit"
                          onClick={handleSubmit}
                          variant="brand"
                        />

                        <Button
                          label={<span>Delete</span>}
                          disabled={isSnapshot}
                          type="button"
                          onClick={handleRemove}
                          variant="destructive"
                        />
                      </div>
                    </section>
                  )}
                </div>
                {/* <div className="" style={{ flexGrow: 1 }}>
                <div className="snapshot_account_defi">
                  <Tabs id="tabs-example-default">
                    <TabsPanel label="Label"></TabsPanel>
                    <TabsPanel label="Label"></TabsPanel>
                  </Tabs>
                </div>
              </div> */}
              </div>
            </form>

            <ActionRemove
              modelData={actionRemoveModal}
              closeModal={setActionRemoveModal}
              removeData={removeAccount}
              isLoading={loadingRemove}
            />
          </>
        );
      }}
    </Formik>
  );
};

export default DetailsTab;
